import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Products & Solutions', path: '/products' },
    { name: 'Support', path: '/support' },
    { name: 'Industries', path: '/industries' },
    { name: 'Contact Us', path: '/contact' },
    { name: 'Customer Portal', path: 'https://books.zohosecure.com/portal/securitybayareallc', external: true },
  ];

  // Pages that should always have white background navbar
  const alwaysWhiteNavPages = ['/apply', '/application-success', '/careers', '/about', '/products', '/industries', '/support', '/contact', '/contact-us', '/maintenance-inquiry', '/maintenance-inquiry-success', '/technical-support'];
  const shouldAlwaysBeWhite = alwaysWhiteNavPages.includes(location.pathname);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || shouldAlwaysBeWhite ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="https://static.readdy.ai/image/7fa2eda8b3881e17b16d0871dce04b24/195408a03611a5b0144ec8988345bbde.png" 
                alt="Security Bay Area Logo" 
                className="h-[150px] w-[250px] object-contain opacity-100"
                style={{ filter: 'none' }}
              />
            </Link>

            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) =>
                (link as { external?: boolean }).external ? (
                  <a
                    key={link.path}
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      isScrolled || shouldAlwaysBeWhite
                        ? 'text-gray-700 hover:text-primary'
                        : 'text-white/90 hover:text-white'
                    }`}
                  >
                    {link.name}
                  </a>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`text-sm font-medium transition-colors whitespace-nowrap ${
                      isScrolled || shouldAlwaysBeWhite
                        ? location.pathname === link.path
                          ? 'text-primary'
                          : 'text-gray-700 hover:text-primary'
                        : location.pathname === link.path
                        ? 'text-white font-semibold'
                        : 'text-white/90 hover:text-white'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              )}
            </div>

            <Link
              to="/contact"
              className={`hidden lg:flex items-center gap-2 px-6 py-3 rounded-full font-medium text-sm transition-all whitespace-nowrap cursor-pointer ${
                isScrolled || shouldAlwaysBeWhite
                  ? 'bg-primary text-white hover:bg-primary-dark'
                  : 'bg-primary text-white hover:bg-primary-dark'
              }`}
            >
              Get Free Quote
              <i className="ri-arrow-right-line"></i>
            </Link>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden text-2xl ${isScrolled || shouldAlwaysBeWhite ? 'text-gray-800' : 'text-white'}`}
              aria-label="Menu"
            >
              <i className={isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 lg:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile Menu */}
      <div
        className={`fixed top-0 right-0 h-full w-[280px] bg-white z-50 lg:hidden transform transition-transform duration-300 ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-6 border-b">
            <span className="text-lg font-semibold text-gray-800">Menu</span>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-2xl text-gray-800"
              aria-label="Close Menu"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>

          <div className="flex flex-col p-6 gap-2 flex-1">
            {navLinks.map((link) =>
              (link as { external?: boolean }).external ? (
                <a
                  key={link.path}
                  href={link.path}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="py-3 px-4 rounded-lg text-base font-medium transition-colors text-gray-700 hover:bg-gray-100 cursor-pointer"
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`py-3 px-4 rounded-lg text-base font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'bg-primary/10 text-primary'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {link.name}
                </Link>
              )
            )}
          </div>

          <div className="p-6 border-t">
            <Link
              to="/contact-us"
              className="flex items-center justify-center gap-2 w-full px-6 py-3 bg-primary text-white rounded-full font-medium text-sm hover:bg-primary-dark transition-all whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;